import { useEffect, useMemo, useState, type ChangeEvent } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, apiClient } from '~/api/client'
import {
    downloadDocument,
    getKnowledgeCorpus,
    uploadDocument,
} from '~/api/documents'
import type { KnowledgeCorpus, KnowledgeCorpusDocument } from '~/api/types'
import {
    CorpusSection,
    SOURCE_TONE,
} from '~/components/knowledge/CorpusSection'
import { type DocumentRow } from '~/components/knowledge/UploadManagementSection'
import { KnowledgeDocumentPanel } from '~/components/KnowledgeDocumentPanel'
import { PageHeader } from '~/components/PageHeader'
import { useToast } from '~/components/ui/toast'

// 지식베이스: 벡터 스토어(Qdrant)에 실제 색인된 RAG 코퍼스 — 검색·상담에 쓰이는 청크를
// 문서 단위로 본다. 각 문서에는 업로드 파이프라인 상태를 documentId로 조인해 함께 표시한다
// (매칭 없으면 카탈로그·CLI 직접 적재). 인입 파이프라인 운영(재색인·삭제·일괄·상태 모니터링)은
// 별도의 「업로드 관리」 페이지에서 다룬다.
export function KnowledgePage() {
    const toast = useToast()

    // 색인 코퍼스(Qdrant) — 청크 기준 RAG 코퍼스. 제목 필터는 클라이언트에서 처리한다.
    const [corpus, setCorpus] = useState<KnowledgeCorpus | null>(null)
    const [corpusLoading, setCorpusLoading] = useState(true)
    const [corpusQ, setCorpusQ] = useState('')

    // 코퍼스 문서 클릭 → 청크 드릴다운 슬라이드오버 + 문서 추가(업로드 등록) 흐름.
    const [selectedDoc, setSelectedDoc] =
        useState<KnowledgeCorpusDocument | null>(null)
    const [uploading, setUploading] = useState(false)

    // 파이프라인 상태 조인용 documents 목록(전체). 상태 열 표시에만 쓰고, 편집은 하지 않는다.
    const [docs, setDocs] = useState<DocumentRow[]>([])
    const docsById = useMemo(() => new Map(docs.map((d) => [d.id, d])), [docs])

    const loadCorpus = () => {
        setCorpusLoading(true)
        getKnowledgeCorpus()
            .then(setCorpus)
            .catch(() => setCorpus(null))
            .finally(() => setCorpusLoading(false))
    }

    // documents 전체(상태 조인용). 실패는 조용히 넘긴다(코퍼스는 그대로 보여야 하므로).
    const loadDocs = () => {
        apiClient
            .get<DocumentRow[]>('/documents')
            .then(setDocs)
            .catch(() => setDocs([]))
    }

    // 코퍼스·문서상태는 화면 진입 시 1회 로드. 연결 불가(available=false)는 에러가 아니라 안내로 다룬다.
    useEffect(() => {
        loadCorpus()
        loadDocs()
    }, [])

    // 문서 추가: 파일 선택 → 원본 등록(uploadDocument). 실제 청크 적재는 워커가 비동기로 한다.
    // 등록 직후엔 코퍼스에 안 보일 수 있어 토스트로 안내하고 상태 목록을 새로고침한다.
    const onUploadFile = async (e: ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0]
        e.target.value = '' // 같은 파일 재선택 허용
        if (!file) return
        setUploading(true)
        try {
            const title = file.name.replace(/\.[^.]+$/, '')
            await uploadDocument(file, { title, domain: 'general' })
            toast.success(
                `'${title}' 문서를 추가했어요. 워커가 인덱싱하면 코퍼스에 반영됩니다.`,
                { action: { label: '업로드 관리 열기', onClick: loadDocs } }
            )
            loadDocs()
        } catch (err) {
            toast.error((err as Error).message)
        } finally {
            setUploading(false)
        }
    }

    // 원본 다운로드(어드민 백엔드 프록시). 청크 드릴다운 패널이 쓴다. 원본이 없으면(CLI 적재
    // 코퍼스처럼 documents 행이 없거나 스토리지 미존재) 404 → 크래시 대신 토스트로 안내한다.
    const downloadOriginal = async (id: string, fallbackName: string) => {
        try {
            await downloadDocument(id, fallbackName)
        } catch (e) {
            const notFound = e instanceof ApiError && e.status === 404
            toast.error(
                notFound ? '원본을 찾을 수 없어요.' : (e as Error).message
            )
        }
    }

    // 코퍼스 소스 키 → 표시 라벨(백엔드 bySource 우선, 없으면 키 그대로).
    const sourceLabel = (key: string) =>
        corpus?.bySource.find((s) => s.key === key)?.label ?? key

    return (
        <div>
            <PageHeader
                title='지식베이스'
                description='색인된 RAG 코퍼스(벡터 스토어) · 문서별 인덱싱 상태'
                actions={
                    <Link
                        to='/content/uploads'
                        className='text-primary text-[13px] font-medium underline-offset-2 hover:underline'>
                        업로드 관리 →
                    </Link>
                }
            />

            <CorpusSection
                corpus={corpus}
                corpusLoading={corpusLoading}
                corpusQ={corpusQ}
                onCorpusQChange={setCorpusQ}
                uploading={uploading}
                onUploadFile={onUploadFile}
                onSelectDoc={setSelectedDoc}
                sourceLabel={sourceLabel}
                docsById={docsById}
            />

            {/* 코퍼스 문서 클릭 → 청크 드릴다운 슬라이드오버(원본 다운로드 포함) */}
            {selectedDoc ? (
                <KnowledgeDocumentPanel
                    documentId={selectedDoc.documentId}
                    title={selectedDoc.title}
                    sourceLabel={sourceLabel(selectedDoc.sourceType)}
                    sourceTone={
                        SOURCE_TONE[selectedDoc.sourceType] ?? 'neutral'
                    }
                    chunkCount={selectedDoc.chunkCount}
                    onClose={() => setSelectedDoc(null)}
                    onDownload={() =>
                        downloadOriginal(
                            selectedDoc.documentId,
                            selectedDoc.title
                        )
                    }
                />
            ) : null}
        </div>
    )
}
